<button
  #launcher
  type="button"
  class="help-fab"
  aria-haspopup="dialog"
  [attr.aria-expanded]="open()"
  aria-label="Help: report an issue"
  title="Help: report an issue"
  (click)="openPanel()">
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
  <span class="help-fab__label">Help</span>
</button>

<p-drawer
  [visible]="open()"
  [position]="position()"
  [modal]="compact()"
  [dismissible]="false"
  [closeOnEscape]="false"
  [showCloseIcon]="false"
  [blockScroll]="compact()"
  [style]="drawerStyle()"
  styleClass="help-drawer"
  (onShow)="onDrawerShown()"
  (onHide)="onDrawerHidden()">
  <ng-template #headless>
    <section
      #panel
      class="help"
      [class.help--dragging]="dragging()"
      role="dialog"
      [attr.aria-modal]="compact()"
      aria-labelledby="help-title"
      (keydown.escape)="onEscape($event)"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)">
      <header class="help__header">
        <h2 id="help-title" class="help__title">Report an issue</h2>
        <button type="button" class="help__close" aria-label="Close help" (click)="requestClose()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </header>

      @if (referenceId(); as ref) {
        <div class="help__body help__done" role="status">
          <svg class="help__done-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="m8 12 3 3 5-6" />
          </svg>
          <h3>Your request has been submitted!</h3>
          <p>We've received your issue and our team will review it.</p>
          <p class="help__reference">Reference: <strong>{{ ref }}</strong></p>
          <button pButton type="button" label="Close" data-help-confirm-focus (click)="requestClose()"></button>
        </div>
      } @else {
        <div class="help__body">
          <p class="help__intro">You can keep working. Nothing on your screen changes while this panel is open.</p>

          <div class="help__field">
            <label for="help-name">Name <span class="help__req" aria-hidden="true">*</span></label>
            <input
              id="help-name"
              pInputText
              type="text"
              autocomplete="name"
              required
              data-help-required
              [value]="name()"
              [attr.aria-invalid]="showErrors() && nameError() ? 'true' : null"
              [attr.aria-describedby]="showErrors() && nameError() ? 'help-name-error' : null"
              (input)="name.set($any($event.target).value)" />
            @if (showErrors() && nameError(); as e) {
              <p id="help-name-error" class="help__error" role="alert">{{ e }}</p>
            }
          </div>

          <div class="help__field">
            <label for="help-email">Email <span class="help__req" aria-hidden="true">*</span></label>
            <input
              id="help-email"
              pInputText
              type="email"
              inputmode="email"
              autocomplete="email"
              required
              data-help-required
              [value]="email()"
              [attr.aria-invalid]="showErrors() && emailError() ? 'true' : null"
              [attr.aria-describedby]="showErrors() && emailError() ? 'help-email-error' : null"
              (input)="email.set($any($event.target).value)" />
            @if (showErrors() && emailError(); as e) {
              <p id="help-email-error" class="help__error" role="alert">{{ e }}</p>
            }
          </div>

          <div class="help__field">
            <label for="help-description">What happened? <span class="help__req" aria-hidden="true">*</span></label>
            <textarea
              id="help-description"
              pTextarea
              [autoResize]="true"
              rows="5"
              required
              data-help-required
              placeholder="Tell us what happened, what you were trying to do, and any error messages you saw."
              [value]="description()"
              [attr.aria-invalid]="showErrors() && descriptionError() ? 'true' : null"
              [attr.aria-describedby]="showErrors() && descriptionError() ? 'help-description-error' : null"
              (input)="description.set($any($event.target).value)"></textarea>
            @if (showErrors() && descriptionError(); as e) {
              <p id="help-description-error" class="help__error" role="alert">{{ e }}</p>
            }
          </div>

          <div class="help__field">
            <span class="help__label" id="help-shots-label">Screenshots <span class="help__hint">(optional)</span></span>
            <div class="help__drop" [class.help__drop--active]="dragging()" aria-labelledby="help-shots-label">
              <button
                type="button"
                pButton
                severity="secondary"
                [outlined]="true"
                icon="pi pi-image"
                label="Add screenshots"
                [disabled]="attachments().length >= limits.maxScreenshots"
                (click)="openFilePicker()"></button>
              <p class="help__hint">
                Paste with Ctrl+V / Cmd+V, drag images here, or choose from your device. Up to
                {{ limits.maxScreenshots }} images, 10 MB each (PNG, JPEG or WebP).
              </p>
              <input
                #fileInput
                class="help__file"
                type="file"
                multiple
                tabindex="-1"
                aria-hidden="true"
                [accept]="acceptAttr"
                (change)="onFilesChosen($event)" />
            </div>

            @if (fileErrors().length) {
              <ul class="help__error help__errors" role="alert">
                @for (e of fileErrors(); track e) {
                  <li>{{ e }}</li>
                }
              </ul>
            }

            @if (attachments().length) {
              <ul class="help__thumbs" aria-label="Attached screenshots">
                @for (a of attachments(); track a.id; let i = $index) {
                  <li class="help__thumb">
                    <img [src]="a.previewUrl" [alt]="'Screenshot ' + (i + 1) + ' preview'" />
                    <button
                      type="button"
                      class="help__thumb-remove"
                      [attr.aria-label]="'Remove screenshot ' + (i + 1)"
                      (click)="removeAttachment(a.id)">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                }
              </ul>
            }
          </div>

          <div class="help__optional">
            <button
              type="button"
              class="help__optional-toggle"
              aria-controls="help-optional-fields"
              [attr.aria-expanded]="showOptional()"
              (click)="showOptional.set(!showOptional())">
              <span>Related patient or visit <span class="help__hint">(optional)</span></span>
              <svg
                class="help__chevron"
                [class.help__chevron--open]="showOptional()"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            @if (showOptional()) {
              <div id="help-optional-fields" class="help__optional-fields">
                <div class="help__field">
                  <label for="help-patient">Patient name</label>
                  <input id="help-patient" pInputText type="text" autocomplete="off" [value]="patientName()" (input)="patientName.set($any($event.target).value)" />
                </div>
                <div class="help__row">
                  <div class="help__field">
                    <label for="help-visit-date">Visit date</label>
                    <input id="help-visit-date" pInputText type="date" [value]="visitDate()" (input)="visitDate.set($any($event.target).value)" />
                  </div>
                  <div class="help__field">
                    <label for="help-visit-id">Visit ID</label>
                    <input id="help-visit-id" pInputText type="text" autocomplete="off" [value]="visitId()" (input)="visitId.set($any($event.target).value)" />
                  </div>
                </div>
                <div class="help__field">
                  <label for="help-visit-details">Additional details</label>
                  <textarea id="help-visit-details" pTextarea [autoResize]="true" rows="2" [value]="visitDetails()" (input)="visitDetails.set($any($event.target).value)"></textarea>
                </div>
                <p class="help__hint">Patient details are stored securely and are never sent to Slack or email.</p>
              </div>
            }
          </div>

          @if (submitError(); as e) {
            <p class="help__error help__submit-error" role="alert">{{ e }}</p>
          }
        </div>

        @if (confirmingDiscard()) {
          <div class="help__confirm" role="alertdialog" aria-labelledby="help-confirm-title" aria-describedby="help-confirm-text">
            <p id="help-confirm-title" class="help__confirm-title">Discard this request?</p>
            <p id="help-confirm-text">What you've entered, including any screenshots, will be lost.</p>
            <div class="help__actions">
              <button pButton type="button" label="Keep editing" data-help-confirm-focus (click)="keepEditing()"></button>
              <button pButton type="button" severity="danger" [outlined]="true" label="Discard" (click)="discardDraft()"></button>
            </div>
          </div>
        } @else {
          <footer class="help__footer">
            <button pButton type="button" severity="secondary" [text]="true" label="Cancel" [disabled]="submitting()" (click)="requestClose()"></button>
            <button
              pButton
              type="button"
              label="Submit request"
              [loading]="submitting()"
              [disabled]="submitting()"
              [attr.aria-busy]="submitting()"
              (click)="submit()"></button>
          </footer>
        }
      }
    </section>
  </ng-template>
</p-drawer>
